import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  CalendarClock,
  Check,
  ChevronDown,
  Flag,
  GitBranch,
  GripVertical,
  Loader2,
  RotateCcw,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/shared/utils/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';
import type { SuggestedAction } from '../types/breaks';
import type {
  BorrowCandidate,
  BorrowImpact,
  GanttBar,
  GanttMove,
  GanttPlan,
  GanttTask,
  GanttTileTone,
  GanttTone,
  ResolutionConsequence,
  ResolutionConsequenceTone,
  ResolutionDetailItem,
  ResolutionDetailItemTone,
  ResolutionOption,
} from '../types/gantt';
import { PlatformGlyph } from './PlatformGlyph';
import {
  ACTION_PLATFORM_LABEL,
  PLATFORM_ICON_BG,
  PLATFORM_TEXT,
} from './break-styles';

interface GanttActionPanelProps {
  plan: GanttPlan;
  /** Whether the schedule change has been applied (drives the bar animation). */
  scheduleApplied: boolean;
  /** Apply the schedule realignment (slides every bar into its proposed slot). */
  onApplySchedule: () => void;
  /** The coordinated follow-up actions (Slack, calendar, …) shown in the footer. */
  coordinatedActions: SuggestedAction[];
  /** Which coordinated actions have already run. */
  appliedActionIds: Set<string>;
  /** Run a single coordinated action. */
  onApplyAction: (action: SuggestedAction) => void;
  /** Run the whole plan at once (schedule + every coordinated action). */
  onApplyAll: () => void;
  /** Whether the entire plan (schedule + all coordinated actions) has run. */
  allApplied: boolean;
}

type ViewMode = 'both' | 'current' | 'proposed';

const GANTT_KEYFRAMES = `
@keyframes gantt-ghost {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.9; }
}
@keyframes gantt-pop {
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.12); }
  100% { transform: scale(1); opacity: 1; }
}`;

/** Fixed geometry so the gutter rows and the chart rows line up exactly. */
const ROW_HEIGHT = 60;
const HEADER_HEIGHT = 56;
/** Height of a team-lane band, inserted before each lane in both columns. */
const LANE_HEIGHT = 34;

/** One team's rows, in authored order. The first lane is the slipping owner's. */
interface GanttLane {
  team: string;
  tasks: GanttTask[];
}

/** Group tasks into lanes by team, preserving first-appearance order. */
function buildLanes(tasks: GanttTask[]): GanttLane[] {
  const laneOrder: string[] = [];
  const tasksByTeam = new Map<string, GanttTask[]>();
  for (const task of tasks) {
    const team = task.team ?? 'Unassigned';
    const existing = tasksByTeam.get(team);
    if (existing) {
      existing.push(task);
    } else {
      tasksByTeam.set(team, [task]);
      laneOrder.push(team);
    }
  }
  return laneOrder.map((team) => ({
    team,
    tasks: tasksByTeam.get(team) as GanttTask[],
  }));
}

/**
 * Layer a resolution option's per-task overrides onto the plan's base tasks,
 * producing the task list this option would arrange. Each `move` can re-position
 * a task's proposed bar, hold it in place (`proposed: null`), or drop it from
 * the release; the option's `addedTasks` (e.g. a borrowed engineer) are appended.
 */
function applyMoves(
  baseTasks: GanttTask[],
  moves: Record<string, GanttMove>
): GanttTask[] {
  return baseTasks.map((task) => {
    const move = moves[task.ref];
    if (!move) return task;
    const dropped = move.dropped ?? task.dropped;
    const proposed = dropped
      ? undefined
      : move.proposed === null
      ? undefined
      : move.proposed ?? task.proposed;
    return {
      ...task,
      proposed,
      dropped,
      statusNote: move.note ?? task.statusNote,
    };
  });
}

function applyOption(
  baseTasks: GanttTask[],
  option: ResolutionOption
): GanttTask[] {
  const moved = applyMoves(baseTasks, option.moves);
  return option.addedTasks ? [...moved, ...option.addedTasks] : moved;
}

/**
 * The vertical center of each task row (keyed by ref), accounting for the lane
 * bands stacked above it - so the grid overlay and dependency arrows line up
 * with the bars. Also returns the total body height.
 */
function buildRowGeometry(
  lanes: GanttLane[],
  laneBandHeight: number
): {
  centerByRef: Map<string, number>;
  bodyHeight: number;
} {
  const centerByRef = new Map<string, number>();
  let offset = 0;
  for (const lane of lanes) {
    offset += laneBandHeight;
    for (const task of lane.tasks) {
      centerByRef.set(task.ref, offset + ROW_HEIGHT / 2);
      offset += ROW_HEIGHT;
    }
  }
  return { centerByRef, bodyHeight: offset };
}

/** Caption under a lane's team name: the owner's name for the slip lane, else a count. */
function laneSubtitle(lane: GanttLane, slipTeam: string | undefined): string {
  const slipTask =
    lane.team === slipTeam
      ? lane.tasks.find((task) => task.tone === 'slip')
      : undefined;
  if (slipTask && slipTask.owner.name !== 'Unassigned') {
    return `${slipTask.owner.name.split(' ')[0]}’s team`;
  }
  const ticketCount = lane.tasks.length;
  return `${ticketCount} ${ticketCount === 1 ? 'ticket' : 'tickets'}`;
}

const firstName = (name: string): string => name.split(' ')[0] ?? name;

/**
 * Days past the committed deadline a headline delta represents, for the option
 * slip-meter visualization. "+10 days" -> 10, "+2 weeks" -> 14, "held" / no
 * match -> 0.
 */
function parseSlipDays(delta: string): number {
  const match = delta.match(/(\d+)\s*(day|week)/i);
  if (!match) return 0;
  const [, amount = '0', unit = 'day'] = match;
  return unit.toLowerCase().startsWith('week')
    ? Number(amount) * 7
    : Number(amount);
}

/**
 * Sharp severity palette for the slip meter + delta chip - how far past the
 * committed date an option lands. None = on date (emerald), small slip (amber),
 * large slip (rose). Drives a vivid fill so the cost reads at a glance.
 */
type SlipSeverity = 'none' | 'low' | 'high';
const SLIP_SEVERITY: Record<
  SlipSeverity,
  { chip: string; fill: string; text: string }
> = {
  none: {
    chip: 'bg-emerald-100 text-emerald-700',
    fill: 'from-emerald-400 to-emerald-500',
    text: 'text-emerald-600',
  },
  low: {
    chip: 'bg-amber-100 text-amber-700',
    fill: 'from-amber-400 to-amber-500',
    text: 'text-amber-600',
  },
  high: {
    chip: 'bg-rose-100 text-rose-700',
    fill: 'from-rose-400 to-rose-500',
    text: 'text-rose-600',
  },
};
const slipSeverity = (days: number): SlipSeverity =>
  days === 0 ? 'none' : days <= 5 ? 'low' : 'high';

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Shift the leading "Mon D" date token of one bar-label segment by `deltaDays`,
 * preserving any trailing text (e.g. "· 2 eng", "target"). Returns the segment
 * unchanged when it has no parseable date. A fixed reference year is used purely
 * to roll month boundaries correctly - the year is never shown.
 */
function shiftLabelSegment(segment: string, deltaDays: number): string {
  const match = segment.match(/^(\s*)([A-Za-z]{3})\s+(\d{1,2})(.*)$/);
  if (!match) return segment;
  const [, leading = '', month = '', day = '', rest = ''] = match;
  const monthIndex = MONTH_ABBR.indexOf(month);
  if (monthIndex < 0) return segment;
  const shifted = new Date(2025, monthIndex, Number(day));
  shifted.setDate(shifted.getDate() + deltaDays);
  return `${leading}${
    MONTH_ABBR[shifted.getMonth()]
  } ${shifted.getDate()}${rest}`;
}

/**
 * Re-date a bar label after a manual drag: every "Mon D" date in the label moves
 * by `deltaDays`, so a bar dragged 3 days later reads its new range. Works off
 * the existing label text, so no separate date model per plan is needed.
 */
function shiftBarLabel(label: string, deltaDays: number): string {
  if (deltaDays === 0) return label;
  return label
    .split('–')
    .map((segment) => shiftLabelSegment(segment, deltaDays))
    .join('–');
}

export function GanttActionPanel({
  plan,
  scheduleApplied,
  onApplySchedule,
  coordinatedActions,
  appliedActionIds,
  onApplyAction,
  onApplyAll,
  allApplied,
}: GanttActionPanelProps) {
  const [mode, setMode] = useState<ViewMode>('both');
  const applied = scheduleApplied;

  // When the plan offers resolution options, the panel becomes a decision
  // surface: the manager picks a strategy and the Gantt re-arranges to match.
  const options = plan.options;
  const defaultOptionId =
    options && options.length > 0
      ? (options.find((option) => option.recommended) ?? options[0])?.id ?? null
      : null;
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(
    defaultOptionId
  );
  // Reset the choice when a different break's plan comes in.
  useEffect(() => {
    setSelectedOptionId(defaultOptionId);
  }, [plan, defaultOptionId]);

  const selectedOption =
    options?.find((option) => option.id === selectedOptionId) ??
    options?.[0] ??
    null;

  // When the selected option borrows an engineer, WHO gets borrowed is itself a
  // choice: each candidate swaps the displaced work, the row that lands on the
  // Gantt, the date the blocker finishes, and the side effect. Default to the
  // recommended candidate; reset whenever the plan or the chosen option changes.
  const borrowCandidates = selectedOption?.borrowCandidates;
  const defaultBorrowCandidateId =
    borrowCandidates && borrowCandidates.length > 0
      ? (
          borrowCandidates.find((candidate) => candidate.recommended) ??
          borrowCandidates[0]
        )?.id ?? null
      : null;
  const [selectedBorrowCandidateId, setSelectedBorrowCandidateId] = useState<
    string | null
  >(defaultBorrowCandidateId);
  // The full-impact modal, opened from the borrowed row's on-calendar dropdown.
  const [borrowDetailOpen, setBorrowDetailOpen] = useState(false);
  useEffect(() => {
    setSelectedBorrowCandidateId(defaultBorrowCandidateId);
    setBorrowDetailOpen(false);
  }, [plan, selectedOptionId, defaultBorrowCandidateId]);
  const selectedBorrowCandidate =
    borrowCandidates?.find(
      (candidate) => candidate.id === selectedBorrowCandidateId
    ) ??
    borrowCandidates?.[0] ??
    null;

  // Manual reschedule: the manager can drag a proposed bar along the day axis.
  // Each drag records a new start unit (snapped to whole days) keyed by task
  // ref; the bar and its date label re-derive from it. Cleared whenever the
  // plan or the chosen option changes, so a drag never leaks across previews.
  const [manualStartByRef, setManualStartByRef] = useState<
    Record<string, number>
  >({});
  useEffect(() => {
    setManualStartByRef({});
  }, [plan, selectedOptionId]);
  const hasManualMoves = Object.keys(manualStartByRef).length > 0;

  // Full pixel width of the time area (bars are positioned as a % of it), so a
  // pointer delta in pixels converts to a delta in axis units (days).
  const chartBodyRef = useRef<HTMLDivElement>(null);
  // Live drag session, held in a ref so the window listeners read it without
  // re-subscribing on every render.
  const dragRef = useRef<{
    ref: string;
    span: number;
    baseStart: number;
    pointerX: number;
    width: number;
    total: number;
  } | null>(null);

  const handleDragMove = useCallback((event: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const deltaUnits =
      ((event.clientX - drag.pointerX) / drag.width) * drag.total;
    const clamped = Math.max(
      0,
      Math.min(drag.total - drag.span, Math.round(drag.baseStart + deltaUnits))
    );
    setManualStartByRef((previous) =>
      previous[drag.ref] === clamped
        ? previous
        : { ...previous, [drag.ref]: clamped }
    );
  }, []);
  const endDrag = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('pointermove', handleDragMove);
    window.removeEventListener('pointerup', endDrag);
  }, [handleDragMove]);
  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handleDragMove);
      window.removeEventListener('pointerup', endDrag);
    },
    [handleDragMove, endDrag]
  );

  const beginBarDrag = (task: GanttTask, clientX: number) => {
    if (applied || !task.proposed) return;
    const width = chartBodyRef.current?.clientWidth ?? 0;
    if (!width) return;
    dragRef.current = {
      ref: task.ref,
      span: task.proposed.span,
      baseStart: manualStartByRef[task.ref] ?? task.proposed.start,
      pointerX: clientX,
      width,
      total: plan.totalUnits,
    };
    window.addEventListener('pointermove', handleDragMove);
    window.addEventListener('pointerup', endDrag);
  };

  const resetManualMoves = () => {
    setManualStartByRef({});
    toast('Reschedule cleared', {
      description: 'Bars are back to Intera’s proposed dates.',
    });
  };

  // Everything below renders from the selected option when present, else from
  // the plan's base fields (so single-plan breaks keep working unchanged).
  const optionTasks = selectedOption
    ? applyOption(plan.tasks, selectedOption)
    : plan.tasks;
  // Layer the chosen borrow candidate on top: apply any schedule shift the
  // borrow imposes (e.g. a slower ramp pushing the blocker later) and append
  // the row that lands them on the Gantt, carrying their "why this person"
  // rationale so it surfaces on hover over their avatar.
  const candidateTasks = selectedBorrowCandidate
    ? [
        ...(selectedBorrowCandidate.moves
          ? applyMoves(optionTasks, selectedBorrowCandidate.moves)
          : optionTasks),
        {
          ...selectedBorrowCandidate.addedTask,
          ownerRationale:
            selectedBorrowCandidate.addedTask.ownerRationale ??
            selectedBorrowCandidate.rationale,
        },
      ]
    : optionTasks;
  // Layer any manual drags on top: move the proposed bar to its dragged start
  // and re-date its label to match.
  const effectiveTasks = candidateTasks.map((task) => {
    const manualStart = manualStartByRef[task.ref];
    if (manualStart === undefined || !task.proposed) return task;
    return {
      ...task,
      proposed: {
        ...task.proposed,
        start: manualStart,
        label: shiftBarLabel(
          task.proposed.label,
          manualStart - task.proposed.start
        ),
      },
    };
  });
  // The chosen borrow candidate re-labels the "Now lands" + "Slip" tiles and the
  // "Side effect" chip so the headline numbers and tradeoffs track WHO is
  // borrowed, not just the recommended default.
  const baseTiles = selectedOption?.impactTiles ?? plan.impactTiles;
  const effectiveTiles: typeof baseTiles = selectedBorrowCandidate
    ? baseTiles.map((tile) => {
        const isBad = selectedBorrowCandidate.outcome.tone === 'bad';
        if (selectedBorrowCandidate.landsValue && /lands/i.test(tile.label)) {
          return {
            ...tile,
            value: selectedBorrowCandidate.landsValue,
            tone: isBad ? 'warn' : tile.tone,
          };
        }
        if (selectedBorrowCandidate.slipValue && /^slip/i.test(tile.label)) {
          return {
            ...tile,
            value: selectedBorrowCandidate.slipValue,
            tone: isBad ? 'warn' : tile.tone,
          };
        }
        return tile;
      })
    : baseTiles;
  const effectiveConsequences: ResolutionConsequence[] = selectedOption
    ? selectedOption.consequences.map((consequence) =>
        consequence.label === 'Side effect' &&
        selectedBorrowCandidate?.sideEffect
          ? selectedBorrowCandidate.sideEffect
          : consequence
      )
    : [];
  const effectiveApplyLabel = selectedOption?.applyLabel ?? plan.applyLabel;

  const blocker = effectiveTasks.find((task) => task.tone === 'slip');

  const lanes = buildLanes(effectiveTasks);
  const slipTeam = blocker?.team;
  const showLaneHeaders = lanes.length > 1;
  const { centerByRef, bodyHeight } = buildRowGeometry(
    lanes,
    showLaneHeaders ? LANE_HEIGHT : 0
  );

  // Inline row actions - the timeline is the control surface.
  const slackOwner = (task: GanttTask) => {
    toast.success(`Pinged ${task.owner.name} on Slack`, {
      description: `Intera drafted the update on ${task.ref} and sent it.`,
    });
  };
  const runRowContext = (task: GanttTask) => {
    if (task.tone === 'slip' || task.tone === 'blocked') {
      onApplySchedule();
    } else {
      toast.success(`Flagged ${task.ref}`, {
        description: `${firstName(
          task.owner.name
        )} was notified the date moved.`,
      });
    }
  };

  // The schedule surface - headline tiles, the cross-team strip, the Gantt, and
  // the view-mode / manual-reschedule controls. Rendered in the right column
  // beside the options when the plan is a decision, or full width for a single
  // plan with no options to choose between.
  // One unified impact block that sits directly on top of the calendar: the
  // selected option's headline numbers and its tradeoff chips, merged into a
  // single card so the manager reads "what it costs" in one place instead of
  // two competing strips.
  const impactSummary = (
    <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white">
      {/* Headline numbers - one calm stat strip (hairline dividers). */}
      <div className="flex flex-wrap items-stretch bg-zinc-50/40">
        {effectiveTiles.map((tile) => (
          <ImpactTile key={tile.label} {...tile} />
        ))}
      </div>

      {/* The chosen option's tradeoffs, on the same card as the numbers. A
          uniform fixed-column grid so every option's strip occupies the exact
          same footprint - switching options never reflows the layout. */}
      {effectiveConsequences.length > 0 && (
        <div className="grid grid-cols-2 gap-px border-t border-zinc-100 bg-zinc-100 sm:grid-cols-4">
          {effectiveConsequences.map((consequence) => {
            const tone = CONSEQUENCE_TONE[consequence.tone];
            return (
              <div
                key={consequence.label}
                className="flex flex-col gap-1 bg-white px-4 py-3"
              >
                <span className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide text-zinc-400">
                  <span
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      tone.dot
                    )}
                  />
                  {consequence.label}
                </span>
                <span
                  className={cn(
                    'text-[12px] font-semibold leading-snug',
                    tone.value
                  )}
                >
                  {consequence.value}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const scheduleBlock = (
    <>
      {/* The Gantt - scrolls horizontally so it is never cramped */}
      <div className="relative overflow-x-auto rounded-2xl border border-zinc-200 bg-white">
        <div className="flex min-w-[820px]">
          {/* Frozen left gutter */}
          <div className="w-[320px] shrink-0 border-e border-zinc-200">
            <GutterHeader />
            {lanes.map((lane) => (
              <div key={lane.team}>
                {showLaneHeaders && (
                  <GutterLaneHeader
                    lane={lane}
                    slipTeam={slipTeam}
                    isOwnerLane={lane.team === slipTeam}
                  />
                )}
                {lane.tasks.map((task) =>
                  borrowCandidates &&
                  task.ref === selectedBorrowCandidate?.addedTask.ref ? (
                    <BorrowGutterRow
                      key={task.ref}
                      task={task}
                      candidates={borrowCandidates}
                      selectedId={selectedBorrowCandidateId}
                      onSelect={setSelectedBorrowCandidateId}
                      onOpenDetail={() => setBorrowDetailOpen(true)}
                    />
                  ) : (
                    <GutterRow
                      key={task.ref}
                      task={task}
                      onSlack={() => slackOwner(task)}
                      onContext={() => runRowContext(task)}
                    />
                  )
                )}
              </div>
            ))}
          </div>

          {/* Time area */}
          <div className="relative flex-1">
            <AxisHeader plan={plan} />
            <div ref={chartBodyRef} className="relative">
              <GridOverlay plan={plan} bodyHeight={bodyHeight} />
              {mode !== 'current' && blocker?.proposed && (
                <DependencyArrows
                  plan={plan}
                  tasks={effectiveTasks}
                  blocker={blocker}
                  centerByRef={centerByRef}
                  bodyHeight={bodyHeight}
                  applied={applied}
                />
              )}
              {lanes.map((lane) => (
                <div key={lane.team}>
                  {showLaneHeaders && (
                    <ChartLaneBand
                      team={lane.team}
                      isOwnerLane={lane.team === slipTeam}
                    />
                  )}
                  {lane.tasks.map((task) => (
                    <BarRow
                      key={task.ref}
                      task={task}
                      total={plan.totalUnits}
                      deadlineUnit={plan.deadlineUnit}
                      mode={mode}
                      applied={applied}
                      draggable={!applied}
                      moved={manualStartByRef[task.ref] !== undefined}
                      onBeginDrag={(clientX) => beginBarDrag(task, clientX)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* View-mode toggle + manual-reschedule controls */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!applied && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-400">
              <GripVertical className="h-3.5 w-3.5" />
              Drag a proposed bar to reschedule it yourself
            </span>
          )}
          {hasManualMoves && !applied && (
            <button
              type="button"
              onClick={resetManualMoves}
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1 text-[11px] font-semibold text-zinc-500 transition-colors hover:border-zinc-300 hover:text-zinc-700"
            >
              <RotateCcw className="h-3 w-3" />
              Reset to Intera’s dates
            </button>
          )}
        </div>
        <div className="inline-flex rounded-full border border-zinc-200 bg-zinc-50 p-0.5">
          <ModeButton active={mode === 'both'} onClick={() => setMode('both')}>
            Current vs proposed
          </ModeButton>
          <ModeButton
            active={mode === 'current'}
            onClick={() => setMode('current')}
          >
            Current
          </ModeButton>
          <ModeButton
            active={mode === 'proposed'}
            onClick={() => setMode('proposed')}
          >
            Proposed
          </ModeButton>
        </div>
      </div>
    </>
  );

  return (
    <div className="rounded-[28px] bg-[linear-gradient(135deg,#eaf4ff_0%,#f4fbff_45%,#ffffff_100%)] p-2 shadow-[0_24px_70px_-40px_rgba(16,27,58,0.4)] sm:p-3">
      <div className="relative overflow-hidden rounded-3xl border border-zinc-200 bg-white p-5 sm:p-7">
        <style>{GANTT_KEYFRAMES}</style>

        <CommandHeader applied={applied} />

        {options && options.length > 0 ? (
          <div className="mt-5">
            {/* Decision header - spans both columns */}
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <h4 className="text-sm font-bold text-zinc-900">
                How do you want to handle this change?
              </h4>
              {applied && (
                <p className="text-[12px] text-zinc-500">
                  Applied: {selectedOption?.name}.
                </p>
              )}
            </div>

            {/* The strategy choice as one compact horizontal selector - three
                comparable outcomes in a row rather than a tall prose rail, so
                the decision reads at a glance and the schedule below it gets the
                full width. Only the chosen option spells out its approach, on a
                single calm line beneath the row. */}
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {options.map((option) => (
                <OptionChoice
                  key={option.id}
                  option={option}
                  selected={option.id === (selectedOption?.id ?? null)}
                  disabled={applied}
                  onSelect={() => setSelectedOptionId(option.id)}
                />
              ))}
            </div>
            {selectedOption?.approach && (
              <p className="mt-3 text-[12.5px] font-semibold leading-relaxed text-zinc-700">
                {selectedOption.approach}
              </p>
            )}

            {/* The chosen option's calendar - full width now that the choice
                sits above instead of beside it. The impact tiles are gone on
                purpose: their labels swapped per option (NOW LANDS vs SHIPS vs
                FEATURES CUT), so switching options made the row jump and the
                manager had to diff it to see what changed. Each option's CAVEAT
                line now carries that difference, read side by side. */}
            <div className="mt-5">{scheduleBlock}</div>

            {/* The per-item breakdown of the chosen option - full width under the
                decision boxes and the calendar, so each change has room to read
                as a grid instead of a tall stack in the rail. */}
            {selectedOption?.detail && (
              <OptionDetailPanel option={selectedOption} />
            )}

            {/* Full borrow impact - opened from the borrowed row's on-calendar
                dropdown ("extend"), so the deep detail lives in a modal instead
                of a permanent panel under the timeline. */}
            {borrowCandidates && (
              <BorrowDetailDialog
                open={borrowDetailOpen}
                onOpenChange={setBorrowDetailOpen}
                candidates={borrowCandidates}
                selectedId={selectedBorrowCandidateId}
                onSelect={setSelectedBorrowCandidateId}
              />
            )}
          </div>
        ) : (
          <div className="mt-5">
            {impactSummary}
            <div className="mt-5">{scheduleBlock}</div>
          </div>
        )}

        {/* The command bar - every action the plan triggers, in one place */}
        <CommandFooter
          tasks={effectiveTasks}
          applyLabel={effectiveApplyLabel}
          laneCount={lanes.length}
          coordinatedActions={coordinatedActions}
          appliedActionIds={appliedActionIds}
          onApplyAction={onApplyAction}
          onApplyAll={onApplyAll}
          allApplied={allApplied}
        />
      </div>
    </div>
  );
}

/* ───────────────────────────── Header ─────────────────────────────── */

function CommandHeader({ applied }: { applied: boolean }) {
  return (
    <div className="relative flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[linear-gradient(135deg,#00AEEF_0%,#0C6ABB_100%)] text-white shadow-sm">
          <GitBranch className="h-4 w-4" strokeWidth={2.5} />
        </span>
        <h3 className="text-lg font-bold tracking-tight text-zinc-900">
          {applied ? 'Plan change handled' : 'Handle the plan change'}
        </h3>
      </div>
      <HeaderLegend />
    </div>
  );
}

/** Compact legend pill group, top-right of the header (reference layout). */
function HeaderLegend() {
  const items: { dot: ReactNode; label: string }[] = [
    {
      dot: <span className="h-2 w-2 rounded-full bg-rose-500" />,
      label: 'Slipped',
    },
    {
      dot: <span className="h-2 w-2 rounded-full bg-slate-400" />,
      label: 'Scheduled',
    },
    {
      dot: (
        <span className="h-2 w-2 rounded-sm border border-dashed border-[#00AEEF] bg-cyan-50" />
      ),
      label: 'Proposed',
    },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full border border-zinc-100 bg-zinc-50 px-4 py-2">
      {items.map((item) => (
        <span
          key={item.label}
          className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-500"
        >
          {item.dot}
          {item.label}
        </span>
      ))}
    </div>
  );
}

/* ─────────────────────── Resolution chooser ───────────────────────── */

/**
 * Tone maps to a single small dot plus the value's text color - never a filled
 * chip. Each consequence renders into a uniform fixed-width grid cell so the
 * strip keeps the exact same footprint as the manager switches between options;
 * only the dot and value carry the cost/caution signal.
 */
const CONSEQUENCE_TONE: Record<
  ResolutionConsequenceTone,
  { dot: string; value: string }
> = {
  good: { dot: 'bg-emerald-500', value: 'text-zinc-700' },
  warn: { dot: 'bg-amber-500', value: 'text-amber-700' },
  bad: { dot: 'bg-rose-500', value: 'text-rose-700' },
  neutral: { dot: 'bg-zinc-300', value: 'text-zinc-700' },
};

/** Filled tone-keyed chip styling - used by the borrow modal's outcome chips. */
const CONSEQUENCE_CHIP: Record<
  ResolutionConsequenceTone,
  { wrap: string; label: string; value: string; icon?: typeof Check }
> = {
  good: {
    wrap: 'border-emerald-200/80 bg-emerald-50',
    label: 'text-emerald-600/80',
    value: 'text-emerald-700',
    icon: Check,
  },
  warn: {
    wrap: 'border-amber-200/80 bg-amber-50',
    label: 'text-amber-600/80',
    value: 'text-amber-700',
    icon: AlertTriangle,
  },
  bad: {
    wrap: 'border-rose-200/80 bg-rose-50',
    label: 'text-rose-600/80',
    value: 'text-rose-700',
    icon: AlertTriangle,
  },
  neutral: {
    wrap: 'border-zinc-200 bg-white',
    label: 'text-zinc-400',
    value: 'text-zinc-700',
  },
};

/**
 * A single, selectable resolution strategy - a compact stacked row sized for the
 * left decision rail. The outcome date sits to the end so the options' dates
 * line up and read as a column; the tradeoffs show as quiet micro-chips so every
 * option exposes its cost without a tall card.
 */
function OptionChoice({
  option,
  selected,
  disabled,
  onSelect,
}: {
  option: ResolutionOption;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  // Headline values read "<date> · <delta>" - split so the date dominates and
  // the delta sits beside it as a sharp, severity-tinted chip.
  const [headlineDate = option.headline.value, ...headlineRest] =
    option.headline.value.split(' · ');
  const headlineDelta = headlineRest.join(' · ');
  const slipDays = parseSlipDays(headlineDelta);
  // When the chip carries a duration slip, color it by magnitude. When it is a
  // qualitative tag (e.g. "full scope", "scope cut") there are no days to parse,
  // so fall back to the option's authored tone so a downside doesn't read green.
  const severity =
    slipDays > 0
      ? SLIP_SEVERITY[slipSeverity(slipDays)]
      : SLIP_SEVERITY[
          option.headline.tone === 'bad'
            ? 'high'
            : option.headline.tone === 'warn'
            ? 'low'
            : 'none'
        ];
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'group relative flex flex-col gap-2.5 rounded-2xl border bg-white p-3.5 text-start transition-all hover:z-30',
        selected
          ? 'border-[#00AEEF] shadow-[0_14px_34px_-22px_rgba(0,174,239,0.5)] ring-1 ring-[#00AEEF]'
          : 'border-zinc-200 hover:border-zinc-300 hover:shadow-sm',
        disabled && 'cursor-default hover:shadow-none'
      )}
    >
      {/* Top line: radio + name, with the recommended badge anchored end-side */}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
            selected ? 'border-[#00AEEF]' : 'border-zinc-300'
          )}
        >
          {selected && <span className="h-2 w-2 rounded-full bg-[#00AEEF]" />}
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-tight text-zinc-900">
          {option.name}
        </span>
        {option.recommended && (
          <span className="group/rec relative inline-flex shrink-0">
            <span className="inline-flex cursor-help items-center gap-1 rounded-full bg-gradient-to-r from-[#00AEEF] to-[#0C6ABB] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm">
              <Sparkles className="h-2.5 w-2.5" />
              Rec
            </span>
            {option.recommendationRationale && (
              <span className="pointer-events-none absolute top-full end-0 z-40 mt-2 hidden w-72 rounded-2xl bg-white p-3.5 text-start shadow-2xl shadow-zinc-900/15 ring-1 ring-zinc-200 backdrop-blur-sm group-hover/rec:block">
                <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#0C6ABB]">
                  <Sparkles className="h-3 w-3" />
                  Why Intera recommends this
                </span>
                <span className="mt-1.5 block text-[12.5px] font-normal normal-case leading-[1.55] text-zinc-600">
                  {option.recommendationRationale}
                </span>
                <span className="absolute -top-[5px] end-6 h-2.5 w-2.5 rotate-45 rounded-[3px] bg-white ring-1 ring-zinc-200" />
              </span>
            )}
          </span>
        )}
      </div>

      {/* The outcome - label + date lead, delta in a severity chip */}
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-wide text-zinc-400">
            {option.headline.label}
          </div>
          <div className="mt-0.5 text-lg font-light leading-none tracking-tight text-zinc-900">
            {headlineDate}
          </div>
        </div>
        {headlineDelta && (
          <span
            className={cn(
              'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold',
              severity.chip
            )}
          >
            {headlineDelta}
          </span>
        )}
      </div>

      {/* The caveat - the one plain-language cost of this option, spelled out on
          every card so all three trade-offs read side by side. */}
      {option.caveat && (
        <div className="mt-auto border-t border-zinc-100 pt-2.5">
          <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-amber-600">
            <AlertTriangle className="h-2.5 w-2.5" />
            Caveat
          </span>
          <p className="mt-1 text-[11.5px] font-medium leading-snug text-zinc-600">
            {option.caveat}
          </p>
        </div>
      )}
    </button>
  );
}

/**
 * The fates an item can meet, each with one plain label and one color. Color is
 * used to LABEL a group, not to tint scattered cards - so the manager reads
 * "these 3 were cut, these 5 just re-sequenced" instead of decoding a rainbow.
 * - rose   = real loss (cut from the release).
 * - amber  = compromised value (ships partial OR deferred to later).
 * - cyan   = new cost introduced (a borrow / fast-follow).
 * - zinc   = re-sequenced at full scope (nothing lost).
 * - emerald = kept untouched.
 */
const FATE_META: Record<
  ResolutionDetailItemTone,
  { label: string; dot: string; text: string }
> = {
  cut: { label: 'Cut from release', dot: 'bg-rose-500', text: 'text-rose-600' },
  partial: {
    label: 'Ships partial',
    dot: 'bg-amber-500',
    text: 'text-amber-600',
  },
  deferred: { label: 'Deferred', dot: 'bg-amber-500', text: 'text-amber-600' },
  added: { label: 'New cost', dot: 'bg-[#00AEEF]', text: 'text-[#0C6ABB]' },
  moved: {
    label: 'Re-sequenced',
    dot: 'bg-zinc-400',
    text: 'text-zinc-500',
  },
  kept: {
    label: 'Kept intact',
    dot: 'bg-emerald-500',
    text: 'text-emerald-600',
  },
};

const FATE_ORDER: ResolutionDetailItemTone[] = [
  'cut',
  'partial',
  'deferred',
  'added',
  'moved',
  'kept',
];

/**
 * The per-item breakdown, clustered by fate. Each fate becomes a small labeled
 * section (colored dot + label + count) so the grouping the colors imply is
 * actually made explicit, and the cards underneath stay calm and uniform.
 */
function DetailItemGroups({ items }: { items: ResolutionDetailItem[] }) {
  const groups = FATE_ORDER.map((fate) => ({
    fate,
    items: items.filter((item) => item.tone === fate),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const meta = FATE_META[group.fate];
        return (
          <div key={group.fate}>
            <div className="mb-2 flex items-center gap-2">
              <span className={cn('h-2 w-2 rounded-full', meta.dot)} />
              <span
                className={cn(
                  'text-[11px] font-bold uppercase tracking-wide',
                  meta.text
                )}
              >
                {meta.label}
              </span>
              <span className="text-[11px] font-semibold text-zinc-400">
                {group.items.length}
              </span>
              <span className="h-px flex-1 bg-zinc-100" />
            </div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {group.items.map((item) => (
                <DetailItemRow
                  key={`${item.ref ?? ''}${item.title}`}
                  item={item}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * One work item as a calm, uniform card: ref, title, and its status. The fate
 * color now lives on the group header, so the card itself stays quiet. The
 * "what changed" description (and any AI rationale) is held back until hover,
 * keeping the grid scannable instead of a wall of paragraphs.
 */
function DetailItemRow({ item }: { item: ResolutionDetailItem }) {
  const hasReveal = Boolean(item.detail) || Boolean(item.rationale);
  const [subjectName = item.title] = item.title.split(' · ');
  return (
    <div
      className={cn(
        'group/item relative rounded-xl border border-zinc-200 bg-white p-3 transition-shadow hover:shadow-sm',
        hasReveal && 'cursor-help hover:z-20'
      )}
    >
      <div className="flex items-center gap-x-2">
        {item.ref && (
          <span className="shrink-0 text-[11px] font-bold tracking-wide text-zinc-400">
            {item.ref}
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-tight text-zinc-900">
          {item.title}
        </span>
        <span className="shrink-0 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">
          {item.status}
        </span>
      </div>

      {/* What changed + why, on hover - so the default grid stays scannable. */}
      {hasReveal && (
        <span className="pointer-events-none absolute bottom-full start-0 z-40 mb-2 hidden w-72 rounded-2xl bg-white p-3.5 text-start shadow-2xl shadow-zinc-900/15 ring-1 ring-zinc-200 backdrop-blur-sm group-hover/item:block">
          {item.detail && (
            <span className="block text-[12.5px] font-normal leading-[1.55] text-zinc-600">
              {item.detail}
            </span>
          )}
          {item.rationale && (
            <>
              <span
                className={cn(
                  'flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#0C6ABB]',
                  item.detail && 'mt-2.5'
                )}
              >
                <Sparkles className="h-3 w-3" />
                Why {subjectName}?
              </span>
              <span className="mt-1 block text-[12.5px] font-normal leading-[1.55] text-zinc-600">
                {item.rationale}
              </span>
            </>
          )}
          <span className="absolute -bottom-[5px] start-6 h-2.5 w-2.5 rotate-45 rounded-[3px] bg-white ring-1 ring-zinc-200" />
        </span>
      )}
    </div>
  );
}

/**
 * The downstream cost of borrowing an engineer, behind an explicit toggle. The
 * borrow's saved days are paid for by someone else's queue - this reveals
 * exactly whose, and what it in turn delays, so it's an inspectable cost rather
 * than a footnote.
 */
function BorrowImpactReveal({ impact }: { impact: BorrowImpact }) {
  const [open, setOpen] = useState(false);
  const borrowedName = firstName(impact.engineer.name);
  return (
    <div className="mt-5 border-t border-zinc-200 pt-4">
      <button
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-start"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
          <Users className="h-3.5 w-3.5" />
        </span>
        <span className="flex-1">
          <span className="text-[13px] font-bold text-zinc-900">
            See what moving {borrowedName} impacts
          </span>
          <span className="ms-2 text-[12px] text-zinc-500">
            {open ? 'Hide the displaced work' : `${impact.items.length} items`}
          </span>
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-zinc-400 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="mt-3 rounded-xl border border-amber-200/70 bg-amber-50/40 p-4">
          <div className="flex items-center gap-2.5">
            <OwnerAvatar owner={impact.engineer} tone="cascade" size={30} />
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-zinc-900">
                {impact.engineer.name} pulled off {impact.source}
              </p>
              <p className="text-[11px] text-zinc-500">{impact.summary}</p>
            </div>
          </div>
          <div className="mt-3">
            <DetailItemGroups items={impact.items} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One borrow candidate as a selectable card - avatar, name, where they're pulled
 * from, and the headline outcome chip (e.g. "Holds Jun 4" vs "Slips to Jun 11")
 * so the date cost of each pick reads at a glance. Used in the full-impact modal.
 */
function BorrowCandidateChip({
  candidate,
  isSelected,
  onSelect,
}: {
  candidate: BorrowCandidate;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const outcomeChip = CONSEQUENCE_CHIP[candidate.outcome.tone];
  const OutcomeIcon = outcomeChip.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      className={cn(
        'flex flex-col gap-2 rounded-xl border p-3 text-start transition-all',
        isSelected
          ? 'border-[#00AEEF] bg-[#00AEEF]/[0.06] shadow-sm ring-1 ring-[#00AEEF]/30'
          : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
      )}
    >
      <div className="flex items-center gap-2">
        <OwnerAvatar owner={candidate.engineer} tone="cascade" size={30} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[12px] font-bold text-zinc-900">
              {candidate.engineer.name}
            </span>
            {candidate.recommended && (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gradient-to-r from-[#00AEEF] to-[#0C6ABB] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-white">
                <Sparkles className="h-2 w-2" />
                Rec
              </span>
            )}
          </div>
          <p className="truncate text-[11px] text-zinc-500">
            from {candidate.source}
          </p>
        </div>
        {isSelected && <Check className="h-4 w-4 shrink-0 text-[#00AEEF]" />}
      </div>
      <span
        className={cn(
          'inline-flex w-fit items-center gap-1 rounded-md border px-1.5 py-0.5',
          outcomeChip.wrap
        )}
      >
        {OutcomeIcon && (
          <OutcomeIcon className={cn('h-2.5 w-2.5', outcomeChip.value)} />
        )}
        <span
          className={cn(
            'text-[9px] font-bold uppercase tracking-wide',
            outcomeChip.label
          )}
        >
          {candidate.outcome.label}
        </span>
        <span className={cn('text-[11px] font-semibold', outcomeChip.value)}>
          {candidate.outcome.value}
        </span>
      </span>
    </button>
  );
}

/**
 * The chosen borrow spelled out: who pays, the AI case for the pick, and every
 * displaced work item. The body of the full-impact modal.
 */
function BorrowCandidateDetail({ candidate }: { candidate: BorrowCandidate }) {
  return (
    <div className="rounded-xl border border-amber-200/70 bg-amber-50/40 p-4">
      <div className="flex items-center gap-2.5">
        <OwnerAvatar owner={candidate.engineer} tone="cascade" size={30} />
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-zinc-900">
            {candidate.engineer.name} pulled off {candidate.source}
          </p>
          <p className="text-[11px] text-zinc-500">{candidate.summary}</p>
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-[#00AEEF]/20 bg-white/70 p-3">
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#0C6ABB]">
          <Sparkles className="h-3 w-3" />
          Why {firstName(candidate.engineer.name)}
        </span>
        <p className="mt-1 text-[12px] leading-relaxed text-zinc-700">
          {candidate.rationale}
        </p>
      </div>

      <div className="mt-3">
        <DetailItemGroups items={candidate.items} />
      </div>
    </div>
  );
}

/**
 * The borrowed engineer's row on the calendar, with WHO is borrowed turned into
 * an on-timeline control: the avatar is a dropdown (its profile picture carries a
 * cyan caret) that swaps the person right where their bar sits, and a "See full
 * impact" action opens the modal for the deep breakdown.
 */
function BorrowGutterRow({
  task,
  candidates,
  selectedId,
  onSelect,
  onOpenDetail,
}: {
  task: GanttTask;
  candidates: BorrowCandidate[];
  selectedId: string | null;
  onSelect: (candidateId: string) => void;
  onOpenDetail: () => void;
}) {
  const [open, setOpen] = useState(false);
  const selected =
    candidates.find((candidate) => candidate.id === selectedId) ??
    candidates[0];
  return (
    <div
      className="group relative flex items-center gap-3 border-t border-zinc-100 px-4"
      style={{ height: ROW_HEIGHT }}
    >
      <div className="group/bavatar relative shrink-0">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Change who pairs on the blocker"
              className="relative rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#00AEEF]/50"
            >
              <OwnerAvatar owner={task.owner} tone={task.tone} size={34} />
              <span className="absolute -bottom-0.5 -end-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-[#00AEEF] text-white shadow-sm">
                <ChevronDown className="h-2.5 w-2.5" />
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={8}
            className="w-72 rounded-xl border-zinc-200 p-2 shadow-xl"
          >
            <p className="flex items-center gap-1.5 px-2 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wide text-zinc-400">
              <Users className="h-3 w-3" />
              Who pairs on the blocker
            </p>
            <div className="flex flex-col gap-0.5">
              {candidates.map((candidate) => {
                const isSelected = candidate.id === (selected?.id ?? null);
                const outcomeChip = CONSEQUENCE_CHIP[candidate.outcome.tone];
                const OutcomeIcon = outcomeChip.icon;
                return (
                  <button
                    key={candidate.id}
                    type="button"
                    onClick={() => {
                      onSelect(candidate.id);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg p-2 text-start transition-colors',
                      isSelected ? 'bg-[#00AEEF]/[0.08]' : 'hover:bg-zinc-50'
                    )}
                  >
                    <OwnerAvatar
                      owner={candidate.engineer}
                      tone="cascade"
                      size={30}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-[12px] font-bold text-zinc-900">
                          {candidate.engineer.name}
                        </span>
                        {candidate.recommended && (
                          <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-gradient-to-r from-[#00AEEF] to-[#0C6ABB] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-white">
                            <Sparkles className="h-2 w-2" />
                            Rec
                          </span>
                        )}
                      </div>
                      <span
                        className={cn(
                          'mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold',
                          outcomeChip.value
                        )}
                      >
                        {OutcomeIcon && <OutcomeIcon className="h-2.5 w-2.5" />}
                        {candidate.outcome.label} {candidate.outcome.value}
                      </span>
                    </div>
                    {isSelected && (
                      <Check className="h-4 w-4 shrink-0 text-[#00AEEF]" />
                    )}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenDetail();
              }}
              className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-200 py-2 text-[12px] font-semibold text-[#0C6ABB] transition-colors hover:bg-[#00AEEF]/[0.06]"
            >
              Compare all pairings
              <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
            </button>
          </PopoverContent>
        </Popover>

        {/* Why this person - the AI case for the pick, on hover over the avatar
            (independent of the swap dropdown, which only opens on click). */}
        {!open && selected?.rationale && (
          <span className="pointer-events-none absolute bottom-full start-0 z-40 mb-2 hidden w-72 rounded-2xl bg-white p-3.5 text-start shadow-2xl shadow-zinc-900/15 ring-1 ring-zinc-200 backdrop-blur-sm group-hover/bavatar:block">
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#0C6ABB]">
              <Sparkles className="h-3 w-3" />
              Why {firstName(selected.engineer.name)}?
            </span>
            <span className="mt-1.5 block text-[12.5px] font-normal leading-[1.55] text-zinc-600">
              {selected.rationale}
            </span>
            <span className="absolute -bottom-[5px] start-6 h-2.5 w-2.5 rotate-45 rounded-[3px] bg-white ring-1 ring-zinc-200" />
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold leading-snug text-zinc-900">
          {task.description}
        </p>
        <div className="mt-0.5 flex items-center gap-1.5">
          {!task.hideRef && !!task.ref && (
            <>
              <span className="shrink-0 text-[11px] font-medium text-zinc-400">
                {task.ref}
              </span>
              <span className="text-zinc-300">·</span>
            </>
          )}
          <span className="truncate text-[11px] font-medium text-zinc-500">
            {task.owner.name}
          </span>
          {task.tag && (
            <span className="shrink-0 rounded bg-zinc-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-zinc-500">
              {task.tag}
            </span>
          )}
        </div>
      </div>

      {/* Explicit affordance beside the row: same dropdown, spelled out */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shrink-0 rounded-lg border border-zinc-200 bg-white/95 px-2 py-1 text-[11px] font-semibold text-zinc-600 opacity-0 shadow-sm transition-opacity duration-150 hover:bg-zinc-50 group-hover:opacity-100"
      >
        Swap
      </button>
    </div>
  );
}

/**
 * The "extend" view: the full borrow impact in a modal, opened from the
 * on-calendar dropdown. The candidate chips let the manager compare and switch
 * here too; the body shows the selected pick's rationale and displaced work.
 */
function BorrowDetailDialog({
  open,
  onOpenChange,
  candidates,
  selectedId,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  candidates: BorrowCandidate[];
  selectedId: string | null;
  onSelect: (candidateId: string) => void;
}) {
  const selected =
    candidates.find((candidate) => candidate.id === selectedId) ??
    candidates[0];
  if (!selected) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="border-zinc-200">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px] font-bold text-zinc-900">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Users className="h-3.5 w-3.5" />
            </span>
            Who pairs on the blocker
          </DialogTitle>
          <DialogDescription className="text-[12px] text-zinc-500">
            Pick who gets borrowed - the delivery date, the displaced work, and
            the side effect all change with the choice.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 sm:grid-cols-3">
          {candidates.map((candidate) => (
            <BorrowCandidateChip
              key={candidate.id}
              candidate={candidate}
              isSelected={candidate.id === selected.id}
              onSelect={() => onSelect(candidate.id)}
            />
          ))}
        </div>

        <BorrowCandidateDetail candidate={selected} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * The bottom line for the selected option, on a progressive-disclosure reveal:
 * the one-sentence trade reads by default (the consequence a manager actually
 * decides on), and the full per-item breakdown - which otherwise restates what
 * the calendar already shows - stays tucked behind a toggle so the resting view
 * keeps a single focus instead of competing panels.
 */
function OptionDetailPanel({ option }: { option: ResolutionOption }) {
  const detail = option.detail;
  const [movesOpen, setMovesOpen] = useState(false);
  if (!detail) return null;
  const movedItemCount = detail.items.length;
  return (
    <div className="mt-5 rounded-2xl border border-zinc-200 bg-zinc-50/60 p-4 sm:p-5">
      {/* Heading + the on-demand toggle for the per-item breakdown */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h5 className="text-[13px] font-bold text-zinc-900">
          {detail.heading}
        </h5>
        {movedItemCount > 0 && (
          <button
            type="button"
            onClick={() => setMovesOpen((wasOpen) => !wasOpen)}
            aria-expanded={movesOpen}
            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1 text-[11px] font-semibold text-zinc-600 transition-colors hover:border-zinc-300 hover:text-zinc-900"
          >
            {movesOpen ? 'Hide what moves' : 'See what moves'}
            <span className="font-bold text-zinc-400">{movedItemCount}</span>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 text-zinc-400 transition-transform',
                movesOpen && 'rotate-180'
              )}
            />
          </button>
        )}
      </div>

      {/* The explicit trade - the one consequence that always reads */}
      {detail.trade && (
        <div className="relative mt-4 overflow-hidden rounded-xl border border-[#00AEEF]/30 bg-gradient-to-br from-[#00AEEF]/[0.08] to-transparent p-3.5">
          <span className="absolute inset-y-0 start-0 w-1 bg-gradient-to-b from-[#00AEEF] to-[#0C6ABB]" />
          <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#0C6ABB]">
            <Flag className="h-3 w-3" />
            The trade
          </span>
          <p className="mt-1.5 text-[12px] leading-relaxed text-zinc-700">
            {detail.trade}
          </p>
        </div>
      )}

      {/* Per-item breakdown - revealed on demand, clustered by fate. The
          calendar already shows these moves; this is the itemized backup a
          manager opens only when they want to inspect every row. */}
      {movesOpen && (
        <div className="mt-5 border-t border-zinc-200 pt-5">
          <DetailItemGroups items={detail.items} />
        </div>
      )}

      {/* Borrow cost - legacy single-owner reveal when no candidate picker */}
      {option.borrowImpact && !option.borrowCandidates && (
        <BorrowImpactReveal impact={option.borrowImpact} />
      )}
    </div>
  );
}

/* ─────────────────────────── Impact tiles ─────────────────────────── */

function ImpactTile({
  label,
  value,
  unit,
  tone,
}: {
  label: string;
  value: string;
  unit?: string;
  tone: GanttTileTone;
}) {
  const isWarn = tone === 'warn';
  return (
    <div
      className={cn(
        'relative flex min-w-[132px] flex-1 flex-col justify-center px-5 py-3.5',
        // Hairline divider between cells; first cell has none.
        'border-s border-zinc-200/60 first:border-s-0',
        isWarn && 'bg-rose-50/40'
      )}
    >
      <p
        className={cn(
          'flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em]',
          isWarn ? 'text-rose-500' : 'text-zinc-400'
        )}
      >
        {isWarn && <AlertTriangle className="h-3 w-3" />}
        {label}
      </p>
      <p className="mt-1 flex items-baseline gap-1.5">
        <span
          className={cn(
            'text-[28px] font-light leading-none tracking-tight',
            isWarn ? 'text-rose-500' : 'text-zinc-900'
          )}
        >
          {value}
        </span>
        {unit && (
          <span
            className={cn(
              'text-sm font-medium',
              isWarn ? 'text-rose-400' : 'text-zinc-400'
            )}
          >
            {unit}
          </span>
        )}
      </p>
    </div>
  );
}

/* ───────────────────────────── Gutter ─────────────────────────────── */

function GutterHeader() {
  // Empty spacer - keeps the gutter aligned with the chart axis header.
  return <div style={{ height: HEADER_HEIGHT }} />;
}

const STATUS_TEXT: Record<GanttTone, string> = {
  slip: 'text-rose-600',
  blocked: 'text-amber-600',
  dependent: 'text-[#0089bd]',
  cascade: 'text-violet-600',
};
/** Colored ring around an owner's avatar, carrying the row's status color. */
const AVATAR_RING: Record<GanttTone, string> = {
  slip: 'ring-rose-400',
  blocked: 'ring-zinc-300',
  dependent: 'ring-zinc-300',
  cascade: 'ring-zinc-300',
};

/** Deterministic persona photo from the owner name (null for Unassigned). */
function avatarUrl(name: string): string | null {
  if (!name || name === 'Unassigned') return null;
  return `https://i.pravatar.cc/96?u=${encodeURIComponent(name)}`;
}

/**
 * A persona avatar - a real photo seeded from the owner name, ringed in the
 * row's status color so the person and their status read as one. Falls back to
 * the owner's initials if the photo can't load (offline / blocked).
 */
function OwnerAvatar({
  owner,
  tone,
  size = 36,
  rationale,
}: {
  owner: { name: string; initials: string };
  tone: GanttTone;
  size?: number;
  /**
   * Optional "why this person" rationale - when present the avatar becomes a
   * hover target that reveals an Intera explanation of why this engineer was
   * chosen (e.g. the borrow onto a blocker).
   */
  rationale?: string;
}) {
  const [failed, setFailed] = useState(false);
  const photo = avatarUrl(owner.name);
  // The circle itself must keep overflow-hidden to clip the photo, so when a
  // rationale exists the tooltip is rendered as a SIBLING inside a group wrapper
  // rather than a child of the clipped circle.
  const circle = (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-100 ring-2 ring-offset-1 ring-offset-white',
        AVATAR_RING[tone]
      )}
      style={{ height: size, width: size }}
      title={owner.name}
    >
      {photo && !failed ? (
        <img
          src={photo}
          alt={owner.name}
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="text-[10px] font-bold text-zinc-500">
          {owner.initials}
        </span>
      )}
    </span>
  );

  if (!rationale) {
    return circle;
  }

  return (
    <span className="group/avatar relative inline-flex shrink-0 cursor-help">
      {circle}
      <span className="pointer-events-none absolute bottom-full start-0 z-40 mb-2 hidden w-72 rounded-2xl bg-white p-3.5 text-start shadow-2xl shadow-zinc-900/15 ring-1 ring-zinc-200 backdrop-blur-sm group-hover/avatar:block">
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#0C6ABB]">
          <Sparkles className="h-3 w-3" /> Why {firstName(owner.name)}?
        </span>
        <span className="mt-1.5 block text-[12.5px] font-normal leading-[1.55] text-zinc-600">
          {rationale}
        </span>
        <span className="absolute -bottom-[5px] start-6 h-2.5 w-2.5 rotate-45 rounded-[3px] bg-white ring-1 ring-zinc-200" />
      </span>
    </span>
  );
}

function GutterRow({
  task,
  onSlack,
  onContext,
}: {
  task: GanttTask;
  onSlack: () => void;
  onContext: () => void;
}) {
  // An unassigned task can be picked up by someone: the manager assigns an
  // owner here, so "?" is a choice to resolve rather than a dead placeholder.
  const [assignedOwner, setAssignedOwner] = useState<{
    name: string;
    initials: string;
  } | null>(null);
  const owner = assignedOwner ?? task.owner;
  const hasOwner = owner.name !== 'Unassigned';
  const canAssign = !hasOwner && (task.assignCandidates?.length ?? 0) > 0;
  // Only show the ticket id when the data carries one and doesn't hide it -
  // messy environments track work without a clean ticket number.
  const showRef = !task.hideRef && !!task.ref;
  // Only the slip itself carries the alert color - everything else is neutral
  // context so the one thing that broke stays the single point of emphasis.
  const isSlip = task.tone === 'slip';
  return (
    <div
      className="group relative flex items-center gap-3 border-t border-zinc-100 px-4"
      style={{ height: ROW_HEIGHT }}
    >
      {/* Persona avatar - or a "?" picker when the task has no owner yet */}
      {canAssign ? (
        <AssignOwnerPicker
          candidates={task.assignCandidates ?? []}
          onAssign={setAssignedOwner}
        />
      ) : (
        <OwnerAvatar
          owner={owner}
          tone={task.tone}
          size={34}
          rationale={task.ownerRationale}
        />
      )}

      {/* Work item - title leads, ref + persona name + tag beneath */}
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'truncate text-[13px] font-semibold leading-snug',
            isSlip ? STATUS_TEXT.slip : 'text-zinc-900'
          )}
        >
          {task.description || (
            <span className="italic text-zinc-400">Untitled</span>
          )}
        </p>
        <div className="mt-0.5 flex items-center gap-1.5">
          {showRef && (
            <span className="shrink-0 text-[11px] font-medium text-zinc-400">
              {task.ref}
            </span>
          )}
          {hasOwner ? (
            <>
              {showRef && <span className="text-zinc-300">·</span>}
              <span className="truncate text-[11px] font-medium text-zinc-500">
                {owner.name}
              </span>
            </>
          ) : (
            <>
              {showRef && <span className="text-zinc-300">·</span>}
              <span className="truncate text-[11px] font-medium italic text-zinc-400">
                Unassigned
              </span>
            </>
          )}
          {task.tag && (
            <span className="shrink-0 rounded bg-zinc-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-zinc-500">
              {task.tag}
            </span>
          )}
        </div>
      </div>

      {/* Hover-revealed inline actions - trigger the action from the row */}
      {hasOwner && (
        <RowActions task={task} onSlack={onSlack} onContext={onContext} />
      )}
    </div>
  );
}

/**
 * The "?" placeholder avatar for an unassigned task. Clicking it opens a small
 * picker of candidate people; choosing one fills the row's owner in place - so
 * an unowned task is something the manager resolves, not just a gap on the chart.
 */
function AssignOwnerPicker({
  candidates,
  onAssign,
}: {
  candidates: Array<{ name: string; initials: string }>;
  onAssign: (owner: { name: string; initials: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="shrink-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Assign someone to this task"
            className="relative rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#00AEEF]/50"
          >
            <span className="relative inline-flex h-[34px] w-[34px] items-center justify-center rounded-full border-2 border-dashed border-zinc-300 bg-zinc-50 text-zinc-400">
              <span className="text-[14px] font-bold">?</span>
            </span>
            <span className="absolute -bottom-0.5 -end-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-[#00AEEF] text-white shadow-sm">
              <UserPlus className="h-2.5 w-2.5" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-64 rounded-xl border-zinc-200 p-2 shadow-xl"
        >
          <p className="flex items-center gap-1.5 px-2 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wide text-zinc-400">
            <Users className="h-3 w-3" />
            Assign someone
          </p>
          <div className="flex flex-col gap-0.5">
            {candidates.map((candidate) => (
              <button
                key={candidate.name}
                type="button"
                onClick={() => {
                  onAssign(candidate);
                  setOpen(false);
                }}
                className="flex items-center gap-2.5 rounded-lg p-2 text-start transition-colors hover:bg-zinc-50"
              >
                <OwnerAvatar owner={candidate} tone="cascade" size={30} />
                <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-zinc-800">
                  {candidate.name}
                </span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function RowActions({
  task,
  onSlack,
  onContext,
}: {
  task: GanttTask;
  onSlack: () => void;
  onContext: () => void;
}) {
  const isReschedule = task.tone === 'slip' || task.tone === 'blocked';
  const ContextIcon = isReschedule ? CalendarClock : Bell;
  const contextLabel = isReschedule
    ? 'Reschedule'
    : `Notify ${firstName(task.owner.name)}`;
  return (
    <div className="absolute end-3 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-lg border border-zinc-200 bg-white/95 p-1 opacity-0 shadow-sm backdrop-blur-sm transition-opacity duration-150 group-hover:opacity-100">
      <button
        type="button"
        onClick={onSlack}
        title={`Message ${task.owner.name} on Slack`}
        className="flex h-6 w-6 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-fuchsia-50 hover:text-[#4A154B]"
      >
        <PlatformGlyph platform="slack" className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onContext}
        title={contextLabel}
        className="flex h-6 w-6 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-blue-50 hover:text-button-primary"
      >
        <ContextIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ────────────────────────── Team lanes ────────────────────────────── */

/** Frozen-gutter header that opens a team's lane: team name + a context caption. */
function GutterLaneHeader({
  lane,
  slipTeam,
  isOwnerLane,
}: {
  lane: GanttLane;
  slipTeam: string | undefined;
  isOwnerLane: boolean;
}) {
  return (
    <div
      className="flex items-center justify-between gap-2 border-t border-zinc-200 bg-zinc-50/80 px-4"
      style={{ height: LANE_HEIGHT }}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <span
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            isOwnerLane ? 'bg-rose-500' : 'bg-zinc-400'
          )}
        />
        <span className="truncate text-[11px] font-bold uppercase tracking-wide text-zinc-600">
          {lane.team}
        </span>
      </div>
      <span className="shrink-0 text-[10px] font-medium text-zinc-400">
        {laneSubtitle(lane, slipTeam)}
      </span>
    </div>
  );
}

/** The chart-side counterpart to GutterLaneHeader - keeps the rows aligned. */
function ChartLaneBand({
  team,
  isOwnerLane,
}: {
  team: string;
  isOwnerLane: boolean;
}) {
  return (
    <div
      className="flex items-center border-t border-zinc-200 bg-zinc-50/80 px-3"
      style={{ height: LANE_HEIGHT }}
    >
      <span
        className={cn(
          'text-[10px] font-semibold uppercase tracking-wide',
          isOwnerLane ? 'text-zinc-400' : 'text-zinc-300'
        )}
      >
        {team}
      </span>
    </div>
  );
}

/* ──────────────────────────── Axis header ─────────────────────────── */

function AxisHeader({ plan }: { plan: GanttPlan }) {
  const u = (unit: number) => (unit / plan.totalUnits) * 100;
  return (
    <div className="relative" style={{ height: HEADER_HEIGHT }}>
      {/* Sprint groups */}
      <div className="relative h-7">
        {plan.groups.map((group) => (
          <div
            key={group.label}
            className="absolute top-0 flex h-full flex-col items-center justify-center border-s border-zinc-100 first:border-s-0"
            style={{ left: `${u(group.start)}%`, width: `${u(group.span)}%` }}
          >
            <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">
              {group.label}
            </span>
            {group.sublabel && (
              <span className="text-[9px] font-medium text-zinc-300">
                {group.sublabel}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Day ticks + markers */}
      <div className="relative h-7 border-t border-zinc-100">
        {plan.ticks.map((tick) => (
          <span
            key={tick.unit}
            className="absolute top-1.5 -translate-x-1/2 text-[9px] font-medium text-zinc-300"
            style={{ left: `${u(tick.unit + 0.5)}%` }}
          >
            {tick.label}
          </span>
        ))}

        {/* Today pill */}
        <div
          className="absolute -top-0.5 -translate-x-1/2"
          style={{ left: `${u(plan.todayUnit + 0.5)}%` }}
        >
          <span className="rounded-sm bg-zinc-900 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-white shadow-sm">
            Today
          </span>
        </div>

        {/* Deadline flag */}
        <div
          className="absolute top-0 flex -translate-x-1/2 items-center gap-1"
          style={{ left: `${u(plan.deadlineUnit)}%` }}
        >
          <Flag className="h-3 w-3 text-zinc-400" />
          <span className="hidden whitespace-nowrap text-[8px] font-bold uppercase tracking-wide text-zinc-400 sm:inline">
            {plan.deadlineLabel}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Grid + marker overlay ────────────────────── */

function GridOverlay({
  plan,
  bodyHeight,
}: {
  plan: GanttPlan;
  bodyHeight: number;
}) {
  const u = (unit: number) => (unit / plan.totalUnits) * 100;
  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{ height: bodyHeight }}
    >
      {/* Sprint group boundaries */}
      {plan.groups.map(
        (group) =>
          group.start > 0 && (
            <div
              key={group.label}
              className="absolute inset-y-0 w-px bg-zinc-100"
              style={{ left: `${u(group.start)}%` }}
            />
          )
      )}

      {/* Today line */}
      <div
        className="absolute inset-y-0 w-px bg-zinc-400/70"
        style={{ left: `${u(plan.todayUnit + 0.5)}%` }}
      />

      {/* Original-deadline line */}
      <div
        className="absolute inset-y-0 border-s-2 border-dashed border-zinc-300"
        style={{ left: `${u(plan.deadlineUnit)}%` }}
      />
    </div>
  );
}

/* ────────────────────────── Dependency arrows ─────────────────────── */

function DependencyArrows({
  plan,
  tasks,
  blocker,
  centerByRef,
  bodyHeight,
  applied,
}: {
  plan: GanttPlan;
  tasks: GanttTask[];
  blocker: GanttTask;
  centerByRef: Map<string, number>;
  bodyHeight: number;
  applied: boolean;
}) {
  const proposed = blocker.proposed;
  if (!proposed) return null;

  const x = (unit: number) => (unit / plan.totalUnits) * 1000;

  const originX = x(proposed.start + proposed.span);
  const originY = centerByRef.get(blocker.ref) ?? ROW_HEIGHT / 2;
  const stroke = applied ? '#10b981' : '#00AEEF';

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox={`0 0 1000 ${bodyHeight}`}
      preserveAspectRatio="none"
      style={{ height: bodyHeight }}
      aria-hidden="true"
    >
      <defs>
        <marker
          id="gantt-arrow"
          markerWidth="6"
          markerHeight="6"
          refX="4"
          refY="3"
          orient="auto"
        >
          <path d="M0 0 L6 3 L0 6 Z" fill={stroke} />
        </marker>
      </defs>
      {tasks.map((task) => {
        if (!task.dependsOnBlocker || !task.proposed) return null;
        const targetX = x(task.proposed.start);
        const targetY = centerByRef.get(task.ref) ?? ROW_HEIGHT / 2;
        // Orthogonal elbow: step right from the blocker, drop vertically, then
        // run horizontally into the dependent - right-angle bends, no diagonals.
        const bendX = originX + (targetX - originX) / 2;
        return (
          <path
            key={task.ref}
            d={`M ${originX} ${originY} L ${bendX} ${originY} L ${bendX} ${targetY} L ${
              targetX - 6
            } ${targetY}`}
            fill="none"
            stroke={stroke}
            strokeWidth={1.5}
            strokeOpacity={0.55}
            strokeDasharray="5 4"
            markerEnd="url(#gantt-arrow)"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </svg>
  );
}

/* ─────────────────────────────── Bars ─────────────────────────────── */

function BarRow({
  task,
  total,
  deadlineUnit,
  mode,
  applied,
  draggable,
  moved,
  onBeginDrag,
}: {
  task: GanttTask;
  total: number;
  /** Axis unit of the original deadline - drives each bar's "days late" badge. */
  deadlineUnit: number;
  mode: ViewMode;
  applied: boolean;
  /** Whether this row's proposed bar can be dragged to reschedule. */
  draggable: boolean;
  /** Whether the manager has manually moved this bar off its proposed slot. */
  moved: boolean;
  /** Start a drag from the proposed bar at the given pointer X. */
  onBeginDrag: (clientX: number) => void;
}) {
  const showCurrent = mode !== 'proposed';
  const showProposed = !task.dropped && mode !== 'current' && task.proposed;

  return (
    <div
      className="relative border-t border-zinc-100"
      style={{ height: ROW_HEIGHT }}
    >
      {showCurrent && task.current && (
        <CurrentBar
          bar={task.current}
          tone={task.tone}
          total={total}
          dimmed={applied && !task.dropped}
          dropped={task.dropped}
          fromState={Boolean(showProposed) && !applied}
        />
      )}
      {showProposed && task.proposed && (
        <ProposedBar
          bar={task.proposed}
          total={total}
          deadlineUnit={deadlineUnit}
          applied={applied}
          solid={mode === 'proposed'}
          draggable={draggable}
          moved={moved}
          onBeginDrag={onBeginDrag}
        />
      )}
    </div>
  );
}

function barStyle(bar: GanttBar, total: number) {
  return {
    left: `calc(${(bar.start / total) * 100}% + 4px)`,
    width: `calc(${(bar.span / total) * 100}% - 8px)`,
  };
}

function CurrentBar({
  bar,
  tone,
  total,
  dimmed,
  dropped,
  fromState,
}: {
  bar: GanttBar;
  tone: GanttTone;
  total: number;
  dimmed: boolean;
  /** This option drops the work item - render it struck through and muted. */
  dropped?: boolean;
  /** This is the "before" slot of a move - render it muted, as the origin. */
  fromState: boolean;
}) {
  const isSlip = tone === 'slip';
  return (
    <div
      className={cn(
        'absolute top-1/2 flex h-7 -translate-y-1/2 items-center overflow-hidden rounded-full px-3 shadow-sm transition-opacity',
        dropped
          ? 'border border-dashed border-zinc-300 bg-zinc-100 text-zinc-400'
          : isSlip
          ? 'border border-rose-200 bg-rose-100 text-rose-700'
          : 'border border-slate-200 bg-slate-200 text-slate-700',
        dimmed && 'opacity-30',
        fromState && !dimmed && 'opacity-60'
      )}
      style={barStyle(bar, total)}
    >
      <span
        className={cn(
          'truncate text-[11px] font-semibold',
          (fromState || dropped) && 'line-through decoration-1 opacity-90'
        )}
      >
        {bar.label}
      </span>
    </div>
  );
}

function ProposedBar({
  bar,
  total,
  deadlineUnit,
  applied,
  solid,
  draggable,
  moved,
  onBeginDrag,
}: {
  bar: GanttBar;
  total: number;
  /** Axis unit of the original deadline - the badge counts days past it. */
  deadlineUnit: number;
  applied: boolean;
  solid: boolean;
  /** Whether this bar can be dragged along the day axis to reschedule. */
  draggable: boolean;
  /** Whether the manager has manually moved this bar off its proposed slot. */
  moved: boolean;
  /** Start a drag from this bar at the given pointer X. */
  onBeginDrag: (clientX: number) => void;
}) {
  const canDrag = draggable && !applied;
  // A manually-moved bar reads solid (a committed choice), not a pulsing ghost.
  const isSolid = solid || moved;
  // How many days past the original deadline this bar now finishes - the single
  // number that separates one option from another at a glance (e.g. +10d vs +3d).
  const daysLate = Math.round(bar.start + bar.span - deadlineUnit);
  const lateBadgeClass = applied
    ? 'bg-emerald-200/80 text-emerald-800'
    : solid
    ? 'bg-white/25 text-white'
    : moved
    ? 'bg-cyan-200 text-cyan-800'
    : 'bg-cyan-100 text-cyan-700';
  return (
    <div
      onPointerDown={
        canDrag
          ? (event) => {
              event.preventDefault();
              onBeginDrag(event.clientX);
            }
          : undefined
      }
      className={cn(
        'absolute top-1/2 flex h-7 -translate-y-1/2 items-center gap-1 overflow-hidden rounded-full px-3 transition-all duration-500',
        applied
          ? 'border border-emerald-200 bg-emerald-100 text-emerald-700 shadow-sm'
          : moved
          ? 'border border-cyan-400 bg-cyan-100 text-cyan-800 shadow-sm'
          : solid
          ? 'bg-cyan-500 text-white shadow-sm'
          : 'border border-dashed border-cyan-300 bg-cyan-50 text-cyan-700',
        canDrag && 'cursor-grab touch-none select-none active:cursor-grabbing'
      )}
      style={{
        ...barStyle(bar, total),
        ...(applied
          ? { animation: 'gantt-pop 0.5s ease-out' }
          : !isSolid
          ? { animation: 'gantt-ghost 2.4s ease-in-out infinite' }
          : {}),
      }}
    >
      {applied && <Check className="h-3 w-3 shrink-0" strokeWidth={3} />}
      {canDrag && !applied && (
        <GripVertical className="-ms-1 h-3 w-3 shrink-0 opacity-50" />
      )}
      <span className="truncate text-[11px] font-semibold">{bar.label}</span>
      {daysLate > 0 && (
        <span
          className={cn(
            'ms-auto shrink-0 rounded px-1 py-px text-[9px] font-bold tabular-nums',
            lateBadgeClass
          )}
          title={`Finishes ${daysLate} ${
            daysLate === 1 ? 'day' : 'days'
          } past the original deadline`}
        >
          +{daysLate}d
        </span>
      )}
    </div>
  );
}

/* ───────────────────────────── Footer ─────────────────────────────── */

function CommandFooter({
  tasks,
  applyLabel,
  laneCount,
  coordinatedActions,
  appliedActionIds,
  onApplyAction,
  onApplyAll,
  allApplied,
}: {
  tasks: GanttTask[];
  applyLabel: string;
  laneCount: number;
  coordinatedActions: SuggestedAction[];
  appliedActionIds: Set<string>;
  onApplyAction: (action: SuggestedAction) => void;
  onApplyAll: () => void;
  allApplied: boolean;
}) {
  const movingCount = tasks.filter((task) => task.proposed).length;
  const ownerCount = new Set(
    tasks.map((task) => task.owner.name).filter((name) => name !== 'Unassigned')
  ).size;

  // Brief "applying…" beat before the schedule actually flips - makes the
  // one-click realignment feel like real work is happening behind it.
  const [applying, setApplying] = useState(false);
  const applyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (applyTimer.current) clearTimeout(applyTimer.current);
    },
    []
  );
  const handleApplyAll = () => {
    if (applying) return;
    setApplying(true);
    applyTimer.current = setTimeout(() => {
      setApplying(false);
      onApplyAll();
    }, 1300);
  };

  return (
    <div
      className={cn(
        'mt-5 rounded-2xl border p-4 transition-colors',
        allApplied
          ? 'border-emerald-200 bg-emerald-50/50'
          : 'border-zinc-200 bg-zinc-50/70'
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
              allApplied
                ? 'bg-emerald-500 text-white'
                : 'bg-[#00AEEF]/10 text-[#0089bd] ring-1 ring-inset ring-[#00AEEF]/20'
            )}
          >
            {allApplied ? (
              <Check className="h-5 w-5" strokeWidth={3} />
            ) : applying ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Sparkles className="h-5 w-5" />
            )}
          </span>
          <div>
            <p className="text-sm font-semibold text-zinc-900">
              {allApplied
                ? 'Plan applied — Intera handled it'
                : applying
                ? 'Applying realignment…'
                : '1-click schedule realignment'}
            </p>
            <p className="mt-0.5 text-[12px] text-zinc-500">
              {allApplied
                ? `Realigned ${movingCount} tickets and ran ${coordinatedActions.length} follow-ups across ${laneCount} teams.`
                : applying
                ? `Rescheduling ${movingCount} tickets and notifying ${ownerCount} owners…`
                : `Notifies ${ownerCount} owners, reschedules ${movingCount} tickets, realigns ${laneCount} teams.`}
            </p>
          </div>
        </div>

        {!allApplied && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={applying}
              onClick={() =>
                toast('Draft discarded', {
                  description: 'Intera kept the current schedule as-is.',
                })
              }
              className="hidden whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-40 disabled:hover:bg-transparent sm:inline-flex"
            >
              Discard draft
            </button>
            <button
              type="button"
              onClick={handleApplyAll}
              disabled={applying}
              aria-busy={applying}
              className="group flex items-center gap-2 whitespace-nowrap rounded-full bg-[linear-gradient(135deg,#00AEEF_0%,#0C6ABB_100%)] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-px hover:shadow-[0_8px_20px_-6px_rgba(0,174,239,0.5)] active:translate-y-px active:scale-[0.98] disabled:translate-y-0 disabled:cursor-wait disabled:opacity-90 disabled:hover:translate-y-0 disabled:hover:shadow-sm"
            >
              {applying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Applying…
                </>
              ) : (
                <>
                  {applyLabel}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" />
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Coordinated actions - each can run on its own, straight from here */}
      {coordinatedActions.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-200/70 pt-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
            Coordinated actions
          </span>
          {coordinatedActions.map((action) => (
            <CoordinatedActionChip
              key={action.id}
              action={action}
              applied={appliedActionIds.has(action.id)}
              onApply={() => onApplyAction(action)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CoordinatedActionChip({
  action,
  applied,
  onApply,
}: {
  action: SuggestedAction;
  applied: boolean;
  onApply: () => void;
}) {
  if (applied) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[12px] font-semibold text-emerald-700"
        title={action.appliedLabel}
      >
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
        {action.appliedLabel}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onApply}
      title={action.title}
      className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-zinc-600 transition-colors hover:border-zinc-300 hover:bg-zinc-50"
    >
      <span
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded',
          PLATFORM_ICON_BG[action.platform]
        )}
      >
        <PlatformGlyph
          platform={action.platform}
          className={cn('h-3 w-3', PLATFORM_TEXT[action.platform])}
        />
      </span>
      {action.actionLabel}
      <span className="text-[10px] font-medium text-zinc-400">
        {ACTION_PLATFORM_LABEL[action.platform]}
      </span>
    </button>
  );
}

/* ────────────────────────── Misc chrome ───────────────────────────── */

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-3 py-1 text-[11px] font-semibold transition-colors',
        active
          ? 'bg-white text-[#0089bd] shadow-sm'
          : 'text-zinc-400 hover:text-zinc-600'
      )}
    >
      {children}
    </button>
  );
}
